// Modificação para o componente VirtualizedMessageList.tsx para incluir esqueleto de carregamento interno
// Isto é útil para quando já temos o componente renderizado mas ainda estamos carregando mensagens adicionais

import React, { useRef, useEffect, useMemo, useState } from 'react';
import { Brain, Reply, SmilePlus, X } from 'lucide-react';
import { OptimizedMessage } from '../services/api.ts';
import type { InstagramCommentData, InstagramCommentScope } from '../services/instagram.ts';
import { OptimizedMessageContent } from './OptimizedMessageContent.tsx';
import { DateSeparator } from './DateSeparator.tsx';
import { groupMessagesByDay } from '../utils/messageGrouping.ts';
import { canCorrectAgentMessage } from '../utils/messageAttribution.ts';
import { useTheme } from '../contexts/ThemeContext.tsx';

interface VirtualizedMessageListProps {
  messages: OptimizedMessage[];
  isLoading: boolean;
  isLoadingMore?: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onReply?: (message: OptimizedMessage) => void;
  onReact?: (message: OptimizedMessage, emoji: string) => void;
  onCorrectAgentMessage?: (message: OptimizedMessage) => void;
  canManageLearning?: boolean;
  contactId?: number;
  instagramScope?: InstagramCommentScope;
  canReplyComment?: boolean;
  onReplyComment?: (comment: InstagramCommentData) => void;
  endAdornment?: React.ReactNode;
  endAdornmentKey?: string | null;
}

export const VirtualizedMessageList: React.FC<VirtualizedMessageListProps> = ({
  messages,
  isLoading,
  isLoadingMore = false,
  hasMore,
  onLoadMore,
  onReply,
  onReact,
  onCorrectAgentMessage,
  canManageLearning = false,
  contactId = 0,
  instagramScope, canReplyComment, onReplyComment,
  endAdornment,
  endAdornmentKey = null,
}) => {
  const { isDark } = useTheme();
  const actionColors = isDark
    ? 'border-sminy-dark-border bg-sminy-dark-surface text-sminy-dark-text-secondary hover:bg-sminy-dark-selection'
    : 'border-sminy-border bg-white text-sminy-text-secondary hover:bg-sminy-blue-light';
  const actionClass = `grid h-10 w-10 shrink-0 place-items-center rounded-xl border shadow-sm hover:text-sminy-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40 ${actionColors}`;
  const containerRef = useRef<HTMLDivElement>(null);
  const endOfMessagesRef = useRef<HTMLDivElement>(null);
  const prevMessagesLengthRef = useRef<number>(0);
  const prevFirstTimestampRef = useRef<number>(0);
  const prevLastTimestampRef = useRef<number>(0);
  const scrollTopLoadLockRef = useRef(false);
  const isNearBottomRef = useRef(true);
  const [reactionPickerMessageId, setReactionPickerMessageId] = useState<string | null>(null);
  const quickReactions = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

  // Carrega mensagens antigas só após rolagem real para o topo.
  // O IntersectionObserver anterior disparava no mount em algumas conversas.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      isNearBottomRef.current = distanceFromBottom <= 120;

      if (container.scrollTop <= 80 && hasMore && !isLoading && !isLoadingMore && messages.length > 0) {
        if (scrollTopLoadLockRef.current) return;
        scrollTopLoadLockRef.current = true;
        onLoadMore();
      } else if (container.scrollTop > 160) {
        scrollTopLoadLockRef.current = false;
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [hasMore, isLoading, isLoadingMore, messages.length, onLoadMore]);

  // Controlar posição de scroll e comportamento de rolagem automática
  useEffect(() => {
    // Se as mensagens foram redefinidas (mudança de contato), resetar referência
    if (messages.length === 0) {
      prevMessagesLengthRef.current = 0;
      prevFirstTimestampRef.current = 0;
      prevLastTimestampRef.current = 0;
      return;
    }
    
    // Carregamento inicial (primeiro conjunto de mensagens)
    const isInitialLoad = prevMessagesLengthRef.current === 0 && messages.length > 0;
    
    // Verificar se mensagens foram adicionadas ao final (novas mensagens)
    // ou ao início (histórico antigo)
    const messagesAdded = messages.length - prevMessagesLengthRef.current;
    const firstTimestamp = messages[0]?.timestampNumber || 0;
    const lastTimestamp = messages[messages.length - 1]?.timestampNumber || 0;
    const previousFirstTimestamp = prevFirstTimestampRef.current;
    const previousLastTimestamp = prevLastTimestampRef.current;
    
    if (!isLoading && !isLoadingMore && endOfMessagesRef.current) {
      if (isInitialLoad) {
        // No carregamento inicial, sempre rolamos para o final
        endOfMessagesRef.current.scrollIntoView({ behavior: 'auto', block: 'end' });
      } else if (messagesAdded > 0 && prevMessagesLengthRef.current > 0) {
        const wereMessagesPrepended = previousFirstTimestamp > 0 && firstTimestamp < previousFirstTimestamp;
        const areNewMessages = previousLastTimestamp > 0 && lastTimestamp > previousLastTimestamp && !wereMessagesPrepended;
        
        // Só rolar automaticamente para o final se forem novas mensagens
        // e não se estiver carregando histórico antigo
        if (areNewMessages && !hasMore) {
          endOfMessagesRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      }
    }
    
    // Atualizar o contador de mensagens
    prevMessagesLengthRef.current = messages.length;
    prevFirstTimestampRef.current = firstTimestamp;
    prevLastTimestampRef.current = lastTimestamp;
  }, [messages.length, isLoading, isLoadingMore, hasMore, messages]);

  useEffect(() => {
    if (
      endAdornmentKey &&
      isNearBottomRef.current &&
      endOfMessagesRef.current
    ) {
      endOfMessagesRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [endAdornmentKey]);

  // Agrupar mensagens por dia
  const groupedMessages = useMemo(() => groupMessagesByDay(messages), [messages]);

  // Componente de esqueleto de carregamento para mensagens adicionais
  const MessageLoadingSkeleton = () => (
    <div className="space-y-6 py-4 opacity-75">
      {/* Esqueleto de mensagem recebida */}
      <div className="flex items-start animate-pulse">
        <div className="w-8 h-8 rounded-full bg-gray-200 mr-2"></div>
        <div className="max-w-[70%]">
          <div className="bg-gray-200 rounded-2xl p-3 w-36 h-12"></div>
        </div>
      </div>
      
      {/* Esqueleto de mensagem enviada */}
      <div className="flex items-start justify-end animate-pulse">
        <div className="max-w-[70%]">
          <div className="bg-sminy-primary/20 rounded-2xl p-3 w-48 h-14"></div>
        </div>
        <div className="w-8 h-8 rounded-full bg-gray-200 ml-2"></div>
      </div>
    </div>
  );

  return (
    <div 
      ref={containerRef}
      className="flex-1 overflow-y-auto p-4 bg-gray-50 min-h-0"
    >
      {/* Indicador de carregamento no topo (para carregar mensagens mais antigas) */}
      {hasMore && (
        <div className="flex justify-center py-2">
          {isLoadingMore ? (
            <MessageLoadingSkeleton />
          ) : (
            <button
              type="button"
              onClick={onLoadMore}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Carregar mensagens anteriores
            </button>
          )}
        </div>
      )}

      {messages.length === 0 && isLoading ? (
        <div className="h-full flex flex-col justify-center">
          <MessageLoadingSkeleton />
          <MessageLoadingSkeleton />
        </div>
      ) : messages.length === 0 && !isLoading ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
          </div>
          <p className="text-gray-500 text-sm">
            Nenhuma mensagem para exibir
          </p>
        </div>
      ) : (
        // Mensagens agrupadas por data
        groupedMessages.map((group) => (
          <div key={group.dayLabel} className="mb-6">
            <DateSeparator date={group.dayLabel} />
            
            <div className="space-y-3 mt-3">
              {group.messages.map((message, index) => {
                const pickerOpen = reactionPickerMessageId === String(message.id);
                const canReact = Boolean(message.providerMessageId);
                const canReply = Boolean(message.providerMessageId);
                const canTeach = Boolean(onCorrectAgentMessage) && canCorrectAgentMessage(message, canManageLearning, contactId);

                return (
                <div
                  key={`${message.id}_${index}`}
                  className={`flex ${message.fromMe ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`group relative flex max-w-[90%] items-start gap-1 ${message.fromMe ? 'flex-row-reverse' : ''}`}>
                    <div className={`flex min-w-0 max-w-[300px] flex-col [&>*]:max-w-full ${message.fromMe ? 'items-end' : 'items-start'}`}>
                      <OptimizedMessageContent
                        message={message}
                        isOwn={message.fromMe}
                        instagramScope={instagramScope}
                        canReplyComment={canReplyComment}
                        onReplyComment={onReplyComment}
                      />
                    </div>

                    {(onReply || onReact || canTeach) && (
                      <div className="flex shrink-0 items-center gap-0.5 pt-1 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                        {onReply && (
                          <button
                            type="button"
                            onClick={() => canReply && onReply(message)}
                            disabled={!canReply}
                            className={actionClass}
                            title={canReply ? 'Responder' : 'Mensagem ainda sem id do WhatsApp'}
                          >
                            <Reply aria-hidden="true" strokeWidth={1.75} className={`h-5 w-5 ${message.fromMe ? 'scale-x-[-1]' : ''}`} />
                          </button>
                        )}

                        {onReact && (
                          <button
                            type="button"
                            onClick={() => canReact && setReactionPickerMessageId(pickerOpen ? null : String(message.id))}
                            disabled={!canReact}
                            className={actionClass}
                            title={canReact ? 'Reagir' : 'Mensagem ainda sem id do WhatsApp'}
                          >
                            <SmilePlus aria-hidden="true" strokeWidth={1.75} className="h-5 w-5" />
                          </button>
                        )}
                        {canTeach && (
                          <div className="grid h-11 w-11 shrink-0 place-items-center sm:h-10 sm:w-10">
                            <button
                              type="button"
                              onClick={() => { setReactionPickerMessageId(null); onCorrectAgentMessage?.(message); }}
                              className={`${actionClass} relative after:absolute after:-inset-0.5 sm:after:inset-0`}
                              title="Como você responderia?"
                              aria-label="Como você responderia?"
                            >
                              <Brain aria-hidden="true" strokeWidth={1.75} className="h-5 w-5" />
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {pickerOpen && onReact && (
                      <>
                        <button
                          type="button"
                          className="fixed inset-0 z-40 cursor-default"
                          onClick={() => setReactionPickerMessageId(null)}
                          aria-label="Fechar reações"
                        />
                        <div className={`absolute top-8 z-50 flex items-center gap-1 rounded-xl border border-gray-200 bg-white p-1.5 shadow-xl ${
                          message.fromMe ? 'right-8' : 'left-8'
                        }`}>
                          {quickReactions.map((emoji) => (
                            <button
                              key={emoji}
                              type="button"
                              onClick={() => {
                                onReact(message, emoji);
                                setReactionPickerMessageId(null);
                              }}
                              className="grid h-8 w-8 place-items-center rounded-lg text-lg hover:bg-gray-100"
                              title={`Reagir ${emoji}`}
                            >
                              {emoji}
                            </button>
                          ))}
                          <button
                            type="button"
                            onClick={() => {
                              onReact(message, '');
                              setReactionPickerMessageId(null);
                            }}
                            className="grid h-8 w-8 place-items-center rounded-lg text-gray-500 hover:bg-gray-100"
                            title="Remover reação"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      {endAdornment}

      {/* Referência para o final das mensagens (para auto-scroll) */}
      <div ref={endOfMessagesRef} className="h-4" />
    </div>
  );
}
